<template>
  <section class="flex flex-col gap-12 items-center justify-center overflow-hidden px-4 py-16 relative w-full bg-white md:px-16">
    <div class="mx-auto w-full max-w-[1200px]">
      <div class="flex w-full flex-col gap-12">
        <div class="flex flex-col gap-1">
          <p
            v-if="overlineText"
            class="text-sm font-semibold uppercase leading-5 tracking-[1px]"
          >
            {{ overlineText }}
          </p>
          <h2 class="text-[36px] font-medium leading-[44px] tracking-[-0.02em]">
            {{ headingText }}
          </h2>
        </div>

        <p v-if="loading" class="text-base text-neutral-500">
          {{ t('articles.loading') }}
        </p>

        <p v-else-if="articles.length === 0" class="text-base text-neutral-500">
          {{ t('articles.empty_carousel') }}
        </p>

        <div
          v-else
          ref="viewportRef"
          class="news-carousel-viewport w-full cursor-grab overflow-hidden touch-pan-y [&_img]:[-webkit-user-drag:none]"
          :class="viewportClass"
          v-bind="swipeHandlers"
        >
          <div
            data-carousel-track
            class="flex gap-8"
            :class="[trackClass, trackTransitionClass]"
            :style="trackStyle"
          >
            <Link
              v-for="article in articles"
              :key="article.id"
              :href="articleHref(article)"
              class="group flex w-(--card-width) shrink-0 flex-col overflow-hidden rounded-xl no-underline"
            >
              <div class="h-[216px] w-full shrink-0 overflow-hidden rounded-xl bg-neutral-200">
                <img
                  v-if="article.cover_image"
                  :src="article.cover_image"
                  :alt="article.title"
                  draggable="false"
                  class="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                >
                <div
                  v-else
                  class="flex h-full w-full items-center justify-center bg-neutral-100 text-neutral-400"
                >
                  <svg class="h-16 w-16" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="1.5"
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>
              </div>

              <div class="flex flex-col gap-2 py-6">
                <NeutralPill
                  v-if="article.site_context_label"
                  :label="article.site_context_label"
                />
                <h3 class="text-xl font-semibold leading-7 tracking-[-0.02em] text-neutral-900 transition-colors group-hover:opacity-80">
                  {{ article.title }}
                </h3>
                <p v-if="article.excerpt" class="line-clamp-3 text-base leading-6 text-neutral-500">
                  {{ article.excerpt }}
                </p>
                <p v-if="article.published_at" class="text-sm leading-5 text-neutral-500">
                  {{ formatDate(article.published_at) }}
                </p>
              </div>
            </Link>
          </div>
        </div>

        <div
          v-if="!loading && articles.length > pageSize"
          class="relative h-11 w-full"
        >
          <CarouselDots
            class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
            :count="dotCount"
            :active-index="slideIndex"
            :dot-size="6"
            :gap="8"
          >
            <template #default="{ active }">
              <span
                class="size-1.5 shrink-0 rounded-full transition-all duration-300 ease-in-out"
                :class="active ? 'opacity-100 scale-125' : 'opacity-30 scale-100'"
                :style="{ backgroundColor: '#262626' }"
              />
            </template>
          </CarouselDots>

          <button
            type="button"
            class="absolute right-0 top-1/2 size-11 -translate-y-1/2 cursor-pointer disabled:cursor-default"
            :aria-label="t('carousel.articles_next')"
            :disabled="!canGoNext"
            @click="next"
          >
            <div
              class="absolute left-0 top-0 flex size-11 items-center justify-center rounded-full border bg-white transition-opacity"
              :class="[
                accentBorderClass,
                canGoNext ? 'hover:opacity-80' : 'opacity-40',
              ]"
            >
              <svg class="size-5" :class="accentTextClass" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </button>

          <button
            type="button"
            class="absolute right-14 top-1/2 size-11 -translate-y-1/2 cursor-pointer disabled:cursor-default"
            :aria-label="t('carousel.articles_prev')"
            :disabled="!canGoPrev"
            @click="prev"
          >
            <div
              class="absolute left-0 top-0 flex size-11 items-center justify-center rounded-full border bg-white transition-colors"
              :class="[
                accentBorderClass,
                canGoPrev ? accentHoverBgClass : 'opacity-40',
              ]"
            >
              <svg
                class="size-5"
                :class="canGoPrev ? accentTextClass : 'text-neutral-400'"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
              </svg>
            </div>
          </button>
        </div>
      </div>

      <div v-if="buttonLabel && !loading && articles.length" class="mt-12 flex justify-center">
        <Link
          :href="allArticlesHref"
          class="inline-flex h-12 items-center justify-center gap-2 rounded-lg border px-5 py-[14px] text-base font-semibold no-underline transition-colors"
          :class="[accentBorderClass, accentTextClass, accentHoverBgClass]"
        >
          {{ buttonLabel }}
          <svg class="size-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
          </svg>
        </Link>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { Link } from '@inertiajs/vue3';
import NeutralPill from '@/Shared/NeutralPill.vue';
import CarouselDots from '@/Shared/CarouselDots.vue';
import { article as articleRoute, articles_index as articlesIndexRoute } from '@/routes.js';
import { useTranslations } from '@/utils/translations.js';
import { useCarouselSwipe } from '@/utils/useCarouselSwipe.js';
import { carouselTrackTransform } from '@/utils/carouselTrack.js';

const { t } = useTranslations();

const props = defineProps({
  overlineText: { type: String, default: '' },
  headingText:  { type: String, default: 'Aktualności' },
  buttonLabel:  { type: String, default: 'Zobacz wszystkie aktualności' },
  // Stored directly in block data (resource_link); no page-link hydration.
  buttonUrl:    { type: String, default: '' },
  // eslint-disable-next-line vue/prop-name-casing
  site_context: { type: String, default: null },
  // eslint-disable-next-line vue/prop-name-casing
  preview_mode: { type: Boolean, default: false },
});

const ACCENT_BY_SITE = {
  code_medical_center: {
    border: 'border-code-medical-center',
    text: 'text-code-medical-center',
    hover: 'hover:bg-code-medical-center-secondary',
  },
  infertility_treatment_clinic: {
    border: 'border-infertility-clinic',
    text: 'text-infertility-clinic',
    hover: 'hover:bg-infertility-clinic-secondary',
  },
  family_clinic: {
    border: 'border-family-clinic',
    text: 'text-family-clinic',
    hover: 'hover:bg-family-clinic-secondary',
  },
  institute: {
    border: 'border-institute',
    text: 'text-institute',
    hover: 'hover:bg-institute-secondary',
  },
};

const accent = computed(() => (
  ACCENT_BY_SITE[props.site_context] || ACCENT_BY_SITE.infertility_treatment_clinic
));
// Figma carousel controls are neutral #262626 in every context (ring + chevron
// stroke, active dot). Only the hover fill keeps the per-clinic tint.
const accentBorderClass = 'border-[#262626]';
const accentTextClass = 'text-[#262626]';
const accentHoverBgClass = computed(() => accent.value.hover);

const articles = ref([]);
const loading = ref(true);

async function loadArticles() {
  loading.value = true;
  try {
    const query = new URLSearchParams();
    if (props.site_context) query.set('site_context', props.site_context);
    const response = await fetch(`/api/articles?${query.toString()}`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const payload = await response.json();
    articles.value = Array.isArray(payload.articles) ? payload.articles : [];
  } catch {
    articles.value = [];
  } finally {
    loading.value = false;
  }
}

onMounted(loadArticles);

const slideIndex = ref(0);
const pageSize = 3;

const maxSlideIndex = computed(() => Math.max(0, articles.value.length - pageSize));
const dotCount = computed(() => maxSlideIndex.value + 1);

const canGoPrev = computed(() => slideIndex.value > 0);
const canGoNext = computed(() => slideIndex.value < maxSlideIndex.value);

const {
  viewportRef,
  viewportClass,
  trackClass,
  trackTransitionClass,
  swipeHandlers,
} = useCarouselSwipe({
  onNext: () => next(),
  onPrev: () => prev(),
  enabled: () => !loading.value && articles.value.length > pageSize,
  canGoNext,
  canGoPrev,
});

const trackStyle = computed(() => carouselTrackTransform(
  `-1 * ${slideIndex.value} * (var(--card-width) + var(--card-gap))`,
));

function next() {
  if (!canGoNext.value) return;
  slideIndex.value += 1;
}

function prev() {
  if (!canGoPrev.value) return;
  slideIndex.value -= 1;
}

const allArticlesHref = computed(() => {
  if (props.buttonUrl && props.buttonUrl !== '#') return props.buttonUrl;
  return articlesIndexRoute();
});

function articleHref(article) {
  return articleRoute(article.slug);
}

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString('pl-PL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}
</script>

<style scoped>
.news-carousel-viewport {
  --card-width: 100%;
  --card-gap: 2rem;
}

@media (min-width: 768px) {
  .news-carousel-viewport {
    --card-width: calc((100% - 2 * var(--card-gap)) / 3);
  }
}
</style>
